import { useState } from 'react';
import { X, Phone, CheckCircle2 } from 'lucide-react';
import { sendPhoneOtp, verifyPhoneOtp } from '../lib/api';
import { TRUST_VERIFICATION_DELTAS } from '@swipeopinion/shared';

type Stage = 'phone' | 'otp' | 'success';

const PHONE_RE = /^\+1\d{10}$/;

function normalizePhone(input: string): string {
  const digits = input.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return input.trim();
}

function formatPhoneInput(input: string): string {
  const digits = input.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

interface Props {
  onClose: () => void;
  onSuccess: () => void;
}

export default function PhoneVerifyModal({ onClose, onSuccess }: Props) {
  const [stage, setStage] = useState<Stage>('phone');
  const [phoneDisplay, setPhoneDisplay] = useState('');
  const [phoneE164, setPhoneE164] = useState('');
  const [token, setToken] = useState('');
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSendOtp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const normalized = normalizePhone(phoneDisplay);
    if (!PHONE_RE.test(normalized)) {
      setError('Enter a 10-digit US phone number.');
      return;
    }
    setSubmitting(true);
    try {
      await sendPhoneOtp(normalized);
      setPhoneE164(normalized);
      setStage('otp');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send code.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const clean = token.replace(/\D/g, '');
    if (clean.length < 4) {
      setError('Enter the code from your text message.');
      return;
    }
    setSubmitting(true);
    try {
      await verifyPhoneOtp(phoneE164, clean, smsOptIn);
      setStage('success');
      // Give the user a beat to see the success state, then close.
      setTimeout(() => { onSuccess(); onClose(); }, 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
        data-testid="phone-verify-modal"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-[#5B4FE8]" />
            <h3 className="text-lg font-bold font-display text-[#111827]">Verify your phone</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-[#6B7280] hover:text-[#111827]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {stage === 'phone' && (
          <form onSubmit={handleSendOtp} className="space-y-3">
            <p className="text-sm text-[#6B7280]">
              We'll text a 6-digit code to confirm it's yours.
            </p>
            <label className="block text-xs font-medium text-[#111827]">
              US phone number
              <input
                type="tel"
                autoFocus
                inputMode="numeric"
                placeholder="(555) 123-4567"
                value={phoneDisplay}
                onChange={(e) => setPhoneDisplay(formatPhoneInput(e.target.value))}
                className="mt-1 w-full rounded-xl border border-[#E5E7EB] bg-white px-4 py-2.5 text-sm text-[#111827] focus:border-[#5B4FE8] focus:outline-none"
              />
            </label>
            {error && <p className="text-xs text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-full bg-[#5B4FE8] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#4338CA] disabled:opacity-60"
            >
              {submitting ? 'Sending…' : 'Send code'}
            </button>
          </form>
        )}

        {stage === 'otp' && (
          <form onSubmit={handleVerify} className="space-y-3">
            <p className="text-sm text-[#6B7280]">
              Enter the code we just sent to <span className="font-medium text-[#111827]">{phoneE164}</span>.
            </p>
            <label className="block text-xs font-medium text-[#111827]">
              6-digit code
              <input
                type="text"
                autoFocus
                inputMode="numeric"
                maxLength={8}
                placeholder="123456"
                value={token}
                onChange={(e) => setToken(e.target.value.replace(/\D/g, ''))}
                className="mt-1 w-full rounded-xl border border-[#E5E7EB] bg-white px-4 py-2.5 text-center text-lg tracking-widest text-[#111827] focus:border-[#5B4FE8] focus:outline-none"
              />
            </label>
            <label className="flex items-start gap-2 text-xs text-[#6B7280]">
              <input
                type="checkbox"
                checked={smsOptIn}
                onChange={(e) => setSmsOptIn(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Text me when high-value surveys are available. Msg &amp; data rates may apply. Reply STOP to cancel.
              </span>
            </label>
            {error && <p className="text-xs text-red-600">{error}</p>}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setStage('phone'); setToken(''); setError(null); }}
                className="flex-1 rounded-full border border-[#E5E7EB] py-2.5 text-sm font-medium text-[#111827] hover:bg-gray-50"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 rounded-full bg-[#5B4FE8] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#4338CA] disabled:opacity-60"
              >
                {submitting ? 'Verifying…' : 'Verify'}
              </button>
            </div>
          </form>
        )}

        {stage === 'success' && (
          <div className="flex flex-col items-center gap-2 py-4 text-center">
            <CheckCircle2 className="w-10 h-10 text-[#10B981]" />
            <p className="text-sm font-medium text-[#111827]">Phone verified</p>
            <p className="text-xs text-[#6B7280]">+{TRUST_VERIFICATION_DELTAS.PHONE_VERIFIED} trust score added.</p>
          </div>
        )}
      </div>
    </div>
  );
}
